// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// units.ts — three unit types, and no multiplier table anywhere.
//
// THE COUNTER TRIANGLE IS A CONSEQUENCE OF THE STATS, NOT A LOOKUP. Damage is flat subtraction of
// armour, and armour SPALLS: every sixth hit a unit takes removes a permanent point of it. That one
// rule is the whole counter system:
//
//   • An Anvil beats a lone Harrier for ever. 14 − 1 = 13 a hit kills 40 hp in four hits; the
//     Harrier's 5 − 3 = 2 a hit needs a hundred and ten. The Anvil walks away on 198/220.
//   • THREE Harriers beat that same Anvil, because 8.6 hits a second strips armour 3 → 0 in 2.1
//     seconds and their damage per hit goes 2 → 3 → 4 → 5 as it does. The Anvil dies at about seven
//     seconds having killed one of them. Ninety ore killed seventy — and the winner only profits if
//     they hold the field afterwards and salvage the wreck.
//   • An Arbalest deletes a Harrier crossing open ground (two shots at 29 land before it closes) and
//     LOSES to the same Harrier through scrub, because scrub is opaque and the Harrier arrives at a
//     tile and a half with 40 hp against one 29-damage shot.
//
// So the answer to "what beats what" is *how many, and where*, which is the only kind of counter
// system worth having in a game with three units in it.
//
// NOTHING HEALS. Hit points are monotonically non-increasing for a unit's whole life and armour
// never comes back. Veterancy runs backwards here: every unit you own is the worst it has ever been.

import { SUB } from './map';

export const enum UnitType {
  Harrier = 0,
  Arbalest = 1,
  Anvil = 2,
}

export interface UnitSpec {
  id: string;
  name: string;
  /** The build button's short label. */
  tag: string;
  cost: number;
  /** Ticks on the Forge. */
  build: number;
  hp: number;
  armour: number;
  damage: number;
  /** Ticks between shots. */
  interval: number;
  /** Range in TENTHS of a tile. `range2` below is the squared sub-unit form the sim compares. */
  reach: number;
  /** Squared range in sub-units, so the check never needs a square root. */
  range2: number;
  /** Sub-units per tick along a cardinal step. Diagonals are scaled by 181/256. */
  speed: number;
  /** Ticks of silence after moving before it can fire again. Only the Arbalest pays this. */
  setup: number;
  /** Ore its wreck is worth the instant it dies. */
  wreck: number;
  /**
   * Does a living body of this type stop a line of fire?
   *
   * ONLY THE ANVIL DOES, and that is a deliberate, measured narrowing of the original design. If
   * every body blocked, then with twenty-four units on a hundred and ninety-two tiles a 4.5-tile
   * Bresenham line crosses five tiles and is almost always occupied — the Arbalest would spend most
   * of a match silent for reasons the player cannot see, which reads as a broken gun rather than as
   * a mechanic. Restricting it to the one unit that visibly IS a wall keeps every situation the rule
   * exists for (your own screen blinds your own artillery; its wreck goes on blinding it after it
   * dies; walking a body into the gap silences a battery) while making the cause legible at a
   * glance: big things block, small things do not.
   */
  blocks: boolean;
  /** One line, shown on the build button's long-press info and in the how-to-play card. */
  role: string;
}

export const UNITS: readonly UnitSpec[] = [
  {
    id: 'harrier',
    name: 'Harrier',
    tag: 'HAR',
    cost: 30,
    build: 80,
    hp: 40,
    armour: 1,
    damage: 5,
    interval: 7,
    reach: 15,
    range2: 0,
    speed: 14,
    setup: 0,
    wreck: 15,
    blocks: false,
    role: 'Fast, cheap, and the only thing that strips armour quickly. Denies seams and salvages wrecks.',
  },
  {
    id: 'arbalest',
    name: 'Arbalest',
    tag: 'ARB',
    cost: 60,
    build: 160,
    hp: 70,
    armour: 1,
    damage: 30,
    interval: 44,
    reach: 45,
    range2: 0,
    speed: 8,
    setup: 15,
    wreck: 30,
    blocks: false,
    role: 'Punishes open ground at four and a half tiles. Blind in scrub, and silent for a moment after it moves.',
  },
  {
    id: 'anvil',
    name: 'Anvil',
    tag: 'ANV',
    cost: 70,
    build: 180,
    hp: 220,
    armour: 3,
    damage: 14,
    interval: 20,
    reach: 13,
    range2: 0,
    speed: 7,
    setup: 0,
    wreck: 35,
    blocks: true,
    role: 'A wall. It holds a gate, it blocks line of fire — including your own — and it cannot chase anything.',
  },
];

// Derived once from SUB so a change to the sub-unit scale can never leave a range constant behind.
for (const u of UNITS) {
  // Rounded to a whole sub-unit: the simulation compares this on both devices and a `.5` in a
  // derived constant is a decimal literal hiding one level down from the source-hygiene grep.
  const r = Math.round((u.reach * SUB) / 10);
  (u as { range2: number }).range2 = r * r;
}

export const UNIT_IDS: readonly string[] = UNITS.map((u) => u.id);

export function unitOf(id: string | null | undefined): UnitSpec {
  const i = UNIT_IDS.indexOf(typeof id === 'string' ? id : '');
  return UNITS[i >= 0 ? i : 0];
}

/** Hits a unit must take before its next armour step falls away. */
export const SPALL_EVERY = 6;

/** Flat subtraction, floored at 1 — nothing is ever completely immune. */
export const applied = (damage: number, armour: number): number => (damage - armour > 1 ? damage - armour : 1);

/** The Forge. Big, armoured, and it does not spall — buildings need bodies, not a bigger gun. */
export const FORGE_HP = 1000;
export const FORGE_ARMOUR = 3;

/**
 * How a living army is scored when the clock runs out. A unit is worth its cost scaled by the
 * fraction of its hit points it still has, which is why a twelve-unit army at 45% loses on
 * adjudication to a fresh six-unit one — and why it also loses the fight.
 */
export const tonnageOf = (type: number, hp: number): number => ((UNITS[type].cost * hp) / UNITS[type].hp) | 0;
